import { QueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { AssociationType } from "@/shared/domain/association";
import { FileRecord, IngestionStatus } from "@/shared/domain/files";
import { SessionRecord } from "@/shared/domain/sessions";
import { TopicRecord, MessagePathRecord } from "@/shared/domain/topics";
import * as UseFileTopics from "@/shared/domain/topics/hooks/useFileTopics";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";
import { Datasource } from "@/shared/state/visualization";
import {
  DatasetSource,
  FileSource,
  SessionSource,
} from "@/shared/state/visualization/schema/v1";

import { EphemeralWorkspaceState, Session } from "./EphemeralWorkspaceState";

const SESSION_FILE_PAGE_SIZE = 1000;

export interface WorkspaceData {
  files: EphemeralWorkspaceState["files"];
  sessions: EphemeralWorkspaceState["sessions"];
  topics: EphemeralWorkspaceState["topics"];
  messagePaths: EphemeralWorkspaceState["messagePaths"];
}

/**
 * Order two sessions by start time (`min_timestamp_ns`), earliest first, with
 * sessions that have no bound last. It is a `bigint`, so compare with relational
 * operators rather than numeric subtraction.
 */
function compareSessionStart(a: SessionRecord, b: SessionRecord): number {
  if (a.min_timestamp_ns === null && b.min_timestamp_ns === null) {
    return 0;
  }
  if (a.min_timestamp_ns === null) {
    return 1;
  }
  if (b.min_timestamp_ns === null) {
    return -1;
  }
  if (a.min_timestamp_ns < b.min_timestamp_ns) {
    return -1;
  }
  if (a.min_timestamp_ns > b.min_timestamp_ns) {
    return 1;
  }
  return 0;
}

/** First occurrence of each item, keyed by `keyOf`. */
function dedupeBy<T>(items: T[], keyOf: (item: T) => string): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const key = keyOf(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(item);
  }
  return result;
}

export class WorkspaceDataService {
  private domainServices: ReturnType<typeof useDomainServices>;
  private queryClient: QueryClient;
  private resourceOwnerId: string | undefined;

  constructor(
    domainServices: ReturnType<typeof useDomainServices>,
    queryClient: QueryClient,
    resourceOwnerId?: string,
  ) {
    this.domainServices = domainServices;
    this.queryClient = queryClient;
    this.resourceOwnerId = resourceOwnerId;
  }

  async fetchWorkspaceData(
    datasources: Datasource[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const { files, datasets, sessions } = this.partitionDatasource(datasources);

    if (datasets.length > 1) {
      throw new Error(
        "Unable to visualize multiple datasets at once. Please select files from a single dataset or visualize each dataset separately.",
      );
    }

    // Fetch dataset data (or empty)
    const datasetData =
      datasets.length === 1
        ? await this.fetchDatasetWorkspaceData(
            datasets[0].value.datasetId,
            abortController,
          )
        : this.emptyWorkspaceData();

    // Get file IDs that aren't already in dataset
    const datasetFileIds = new Set(datasetData.files.map((f) => f.file_id));
    const additionalFileIds = files
      .map((f) => f.value.fileId)
      .filter((id) => !datasetFileIds.has(id));

    // Fetch additional files (or empty)
    const fileData =
      additionalFileIds.length > 0
        ? await this.fetchFileWorkspaceData(additionalFileIds, abortController)
        : this.emptyWorkspaceData();

    // Expand each session datasource into a Session (its record + files). One
    // unreadable session is skipped rather than failing the whole workspace.
    const sessionData = await Promise.all(
      sessions.map((session) =>
        this.fetchSessionWorkspaceData(
          session.value.sessionId,
          abortController,
        ),
      ),
    );

    // Merge and return
    return this.mergeWorkspaceData(datasetData, fileData, ...sessionData);
  }

  private emptyWorkspaceData(): WorkspaceData {
    return { files: [], sessions: [], topics: [], messagePaths: [] };
  }

  /**
   * Merge parts into one workspace. The flat `files`, `topics`, and
   * `messagePaths` are deduped by id (a file shared by two sessions is fetched
   * per session), but each `Session.files` is left intact so a shared file still
   * appears under every session. Sessions are sorted once, by start time.
   */
  private mergeWorkspaceData(...parts: WorkspaceData[]): WorkspaceData {
    return {
      files: dedupeBy(
        parts.flatMap((part) => part.files),
        (file) => file.file_id,
      ).sort((x, y) =>
        x.relative_path.localeCompare(y.relative_path, undefined, {
          sensitivity: "base",
        }),
      ),
      sessions: parts
        .flatMap((part) => part.sessions)
        .sort((a, b) => compareSessionStart(a.record, b.record)),
      topics: dedupeBy(
        parts.flatMap((part) => part.topics),
        (topic) => topic.topic_id,
      ),
      messagePaths: dedupeBy(
        parts.flatMap((part) => part.messagePaths),
        (messagePath) => messagePath.message_path_id,
      ),
    };
  }

  private extractMessagePaths(topics: TopicRecord[]): MessagePathRecord[] {
    return topics.flatMap((topic) => topic.message_paths);
  }

  private async fetchDatasetWorkspaceData(
    datasetId: string,
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const fileRecords = await this.domainServices.datasetService.listAllFiles(
      datasetId,
      {
        abortSignal: abortController.signal,
      },
    );

    const ingestedFileRecords = this.getIngestedFiles(fileRecords);

    const topicRecords = await this.fetchTopicsForFiles(
      ingestedFileRecords.map((fileRecord) => fileRecord.file_id),
    );

    return {
      files: ingestedFileRecords,
      sessions: [],
      topics: topicRecords,
      messagePaths: this.extractMessagePaths(topicRecords),
    };
  }

  private async fetchFileWorkspaceData(
    fileIds: string[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const [fileRecords, topicRecords] = await Promise.all([
      Promise.all(
        fileIds.map((fileId) =>
          this.domainServices.files.getFileRecord(fileId, {
            abortSignal: abortController.signal,
          }),
        ),
      ),
      this.fetchTopicsForFiles(fileIds),
    ]);

    return {
      files: fileRecords,
      sessions: [],
      topics: topicRecords,
      messagePaths: this.extractMessagePaths(topicRecords),
    };
  }

  /**
   * Expand one session datasource into its files and record. Fetches the record
   * (name + `min_timestamp_ns`) in parallel with the file listing, reuses the
   * file path for records/topics, and packages the result as a single
   * {@link Session}. A read failure is captured and swallowed (the session
   * contributes nothing) so it does not cost the user the rest of the workspace;
   * an abort propagates.
   */
  private async fetchSessionWorkspaceData(
    sessionId: string,
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    try {
      const [record, sessionFiles] = await Promise.all([
        this.domainServices.sessionService.getSessionById(sessionId, {
          abortSignal: abortController.signal,
          resourceOwnerId: this.resourceOwnerId,
        }),
        this.domainServices.sessionService.listAllSessionFiles(sessionId, {
          limit: SESSION_FILE_PAGE_SIZE,
          resourceOwnerId: this.resourceOwnerId,
          abortSignal: abortController.signal,
        }),
      ]);

      const fileData = await this.fetchFileWorkspaceData(
        sessionFiles.map((file) => file.file_id),
        abortController,
      );

      const session: Session = {
        id: sessionId,
        record,
        files: fileData.files,
      };

      return { ...fileData, sessions: [session] };
    } catch (error) {
      if (abortController.signal.aborted || isAbortError(error)) {
        throw error;
      }
      ErrorMonitoringService.captureError(error);
      return this.emptyWorkspaceData();
    }
  }

  private async fetchTopicsForFiles(fileIds: string[]): Promise<TopicRecord[]> {
    const topicRecordsPerFile = await Promise.all(
      fileIds.map((fileId) =>
        this.queryClient.ensureQueryData({
          queryKey: UseFileTopics.getQueryKey(fileId),
          queryFn: ({ signal }) =>
            this.domainServices.topics.getTopicsByAssociation(
              {
                association_id: fileId,
                association_type: AssociationType.File,
              },
              {
                abortSignal: signal,
              },
            ),
        }),
      ),
    );
    return topicRecordsPerFile.flat();
  }
  private getIngestedFiles(fileRecords: FileRecord[]): FileRecord[] {
    return fileRecords.filter(
      (fileRecord) =>
        fileRecord.ingestion_status !== IngestionStatus.NotIngested,
    );
  }

  private partitionDatasource(datasources: Datasource[]): {
    files: FileSource[];
    datasets: DatasetSource[];
    sessions: SessionSource[];
  } {
    return datasources.reduce<{
      files: FileSource[];
      datasets: DatasetSource[];
      sessions: SessionSource[];
    }>(
      (data, datasource) => {
        if (datasource.type === "file") {
          data.files.push(datasource);
        } else if (datasource.type === "session") {
          data.sessions.push(datasource);
        } else {
          data.datasets.push(datasource);
        }
        return data;
      },
      {
        files: [],
        datasets: [],
        sessions: [],
      },
    );
  }
}
